import Api from '@/services/Api';

export default {
    // ==========================================
    // Admin Methods
    // ==========================================

    /**
     * Get all alert rules
     * @returns {Promise} API response with rules array
     */
    getRules() {
        return Api().get('packlist-alerts/rules');
    },

    /**
     * Get a single rule by ID
     * @param {number} ruleId - Rule ID
     * @returns {Promise} API response with rule object
     */
    getRule(ruleId) {
        return Api().get(`packlist-alerts/rules/${ruleId}`);
    },

    /**
     * Create a new alert rule
     * @param {Object} ruleData - Rule data
     * @returns {Promise} API response
     */
    createRule(ruleData) {
        return Api().post('packlist-alerts/rules', ruleData);
    },

    /**
     * Update an existing rule
     * @param {number} ruleId - Rule ID
     * @param {Object} ruleData - Updated rule data
     * @returns {Promise} API response
     */
    updateRule(ruleId, ruleData) {
        return Api().put(`packlist-alerts/rules/${ruleId}`, ruleData);
    },

    /**
     * Delete a rule
     * @param {number} ruleId - Rule ID
     * @returns {Promise} API response
     */
    deleteRule(ruleId) {
        return Api().delete(`packlist-alerts/rules/${ruleId}`);
    },

    /**
     * Upload an image for alerts
     * @param {FormData} formData - Form data with image file
     * @returns {Promise} API response with image URL
     */
    uploadImage(formData) {
        return Api().post('packlist-alerts/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },

    /**
     * Get images for a rule
     * @param {number|string} ruleId - Rule ID or 'all'
     * @returns {Promise} API response with images array
     */
    getImages(ruleId = 'all') {
        return Api().get(`packlist-alerts/images/${ruleId}`);
    },

    /**
     * Delete an image
     * @param {number} imageId - Image ID
     * @returns {Promise} API response
     */
    deleteImage(imageId) {
        return Api().delete(`packlist-alerts/images/${imageId}`);
    },

    /**
     * Get alert logs (Admin only)
     * @param {Object} filters - Filter options
     * @returns {Promise} API response with logs array
     */
    getLogs(filters = {}) {
        const params = new URLSearchParams();
        if (filters.ruleId) params.append('ruleId', filters.ruleId);
        if (filters.limit) params.append('limit', filters.limit);

        return Api().get(`packlist-alerts/logs?${params}`);
    },

    /**
     * Get logs for a specific rule
     * @param {number} ruleId - Rule ID
     * @param {number} limit - Max number of logs to return
     * @returns {Promise} API response with logs array
     */
    getRuleLogs(ruleId, limit = 100) {
        return this.getLogs({ ruleId, limit });
    },

    /**
     * Get statistics for a specific rule
     * @param {number} ruleId - Rule ID
     * @returns {Promise} API response with statistics
     */
    getRuleStats(ruleId) {
        // Stats are already included in the rule object when fetched
        // But we can also get them separately if needed
        return Api().get(`packlist-alerts/rules/${ruleId}`).then(response => {
            return {
                data: {
                    stats: response.data.rule?.stats || {
                        total_shows: 0,
                        unique_users: 0,
                        unique_orders: 0
                    }
                }
            };
        });
    },

    /**
     * Get statistics (Admin only)
     * @returns {Promise} API response with statistics
     */
    getStats() {
        return Api().get('packlist-alerts/stats');
    },

    /**
     * Get filter options (categories, products, countries, SKUs)
     * @returns {Promise} API response with filter options
     */
    getFilterOptions() {
        return Api().get('packlist-alerts/filter-options');
    },

    // ==========================================
    // User Methods
    // ==========================================

    /**
     * Check alerts for an order
     * @param {number} orderId - Order ID
     * @returns {Promise} API response with matching alerts
     */
    checkAlerts(orderId) {
        return Api().post('packlist-alerts/check', { orderId });
    },

    // ==========================================
    // Helper Methods
    // ==========================================

    /**
     * Get priority label in German
     * @param {string} priority - Priority level
     * @returns {string} German label
     */
    getPriorityLabel(priority) {
        const labels = {
            low: 'Niedrig',
            medium: 'Mittel',
            high: 'Hoch',
            critical: 'Kritisch'
        };
        return labels[priority] || priority;
    },

    /**
     * Get priority color
     * @param {string} priority - Priority level
     * @returns {string} Color name
     */
    getPriorityColor(priority) {
        const colors = {
            low: 'grey',
            medium: 'blue',
            high: 'orange darken-1',
            critical: 'red darken-2'
        };
        return colors[priority] || 'grey';
    },

    /**
     * Format date for display
     * @param {string|Date} date - Date to format
     * @returns {string} Formatted date string
     */
    formatDate(date) {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleDateString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    /**
     * Get icon for priority
     * @param {string} priority - Priority level
     * @returns {string} MDI icon name
     */
    getPriorityIcon(priority) {
        const icons = {
            low: 'mdi-information',
            medium: 'mdi-alert-circle-outline',
            high: 'mdi-alert',
            critical: 'mdi-alert-octagon'
        };
        return icons[priority] || 'mdi-information';
    },

    /**
     * Format filter summary
     * @param {Object} rule - Rule object
     * @returns {string} Human-readable filter summary
     */
    getFilterSummary(rule) {
        const parts = [];

        if (rule.filter_skus && rule.filter_skus.length > 0) {
            parts.push(`${rule.filter_skus.length} SKU${rule.filter_skus.length > 1 ? 's' : ''}`);
        }
        if (rule.filter_countries && rule.filter_countries.length > 0) {
            parts.push(`${rule.filter_countries.length} Land/Länder`);
        }
        if (rule.filter_categories && rule.filter_categories.length > 0) {
            parts.push(`${rule.filter_categories.length} Kategorie${rule.filter_categories.length > 1 ? 'n' : ''}`);
        }
        if (rule.filter_products && rule.filter_products.length > 0) {
            parts.push(`${rule.filter_products.length} Produkt${rule.filter_products.length > 1 ? 'e' : ''}`);
        }

        if (parts.length === 0) {
            return 'Keine Filter';
        }

        const filterType = rule.filter_type === 'AND' ? 'UND' : 'ODER';
        return `${parts.join(', ')} (${filterType})`;
    },

    /**
     * Validate rule data before saving
     * @param {Object} ruleData - Rule data to validate
     * @returns {Object} { valid: boolean, errors: string[] }
     */
    validateRule(ruleData) {
        const errors = [];

        if (!ruleData.name || ruleData.name.trim() === '') {
            errors.push('Regelname ist erforderlich');
        }

        if (!ruleData.messageTitle || ruleData.messageTitle.trim() === '') {
            errors.push('Nachrichtentitel ist erforderlich');
        }

        if (!ruleData.messageContent || ruleData.messageContent.trim() === '') {
            errors.push('Nachrichteninhalt ist erforderlich');
        }

        // Check if at least one filter is set
        const hasFilters = (
            (ruleData.filterSkus && ruleData.filterSkus.length > 0) ||
            (ruleData.filterCountries && ruleData.filterCountries.length > 0) ||
            (ruleData.filterCategories && ruleData.filterCategories.length > 0) ||
            (ruleData.filterProducts && ruleData.filterProducts.length > 0)
        );

        if (!hasFilters) {
            errors.push('Mindestens ein Filter muss gesetzt sein');
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }
};
